import type { Article, ArticleTag } from '@club/types/Article'
import type { Category } from '@club/types/Category'
import type { Pagination } from '@club/types/Pagination'
import ArticleImage from '@web/assets/images/articleImage.jpg'
import getBaseUrl from '@web/common/getBaseUrl'

interface RawArticle {
  id: number | string
  title: string
  short_description: string
  content?: string
  created_at: string
  published_at: string
  locked: boolean
  favorite: boolean
  liked: boolean
  likes_count?: number
  comments_count?: number
  new: boolean
  views: number
  autoLoadComments?: boolean
  auto_load_comments?: boolean
  article_image?: { url?: string }
  preview_image?: { thumbnail?: string }
  tags?: Array<{ id: number | string; name: string }>
  categories?: Array<{ id: number | string; title: string }>
  author?: {
    id: number | string
    name: { first: string; full: string; last: string }
    photo?: { url?: string }
  }
  main_category?: {
    id: number
    title: string
    created_at: string
    community?: {
      id: number
      external_id: string
      slug: string
      status: string
      title: string
    }
  }
}

function formattedArticle (raw: unknown): Article {
  const data = raw as RawArticle
  // Аналог клубного $platformUrl (Nuxt-плагин поверх PLATFORM_URL_API): картинки
  // приходят от API относительными путями и должны резолвиться на хост платформы.
  // getBaseUrl делает то же самое (VUE_APP_BASE_URL) и дополнительно пропускает
  // уже абсолютные URL как есть.
  const $platformUrl = (url: string) => getBaseUrl(url)

  let tags: ArticleTag[] = []

  if (Array.isArray(data.tags)) {
    tags = data.tags.map(tag => {
      return {
        id: Number(tag.id),
        name: String(tag.name)
      }
    })
  }

  let categories: Category[] = []
  if (Array.isArray(data.categories)) {
    categories = data.categories.map((category) => {
      return {
        id: Number(category.id),
        title: String(category.title)
      } as Category
    })
  }

  const article: Article = {
    id: Number(data.id),
    article_image: String(data?.article_image?.url ? $platformUrl(data?.article_image?.url) : ArticleImage),
    preview_image: String(data?.preview_image?.thumbnail ? $platformUrl(decodeURIComponent(data?.preview_image?.thumbnail)) : ArticleImage),
    categories,
    title: String(data.title),
    short_description: String(data.short_description),
    description: String(data.content || ''),
    tags,
    created_at: String(data.created_at),
    published_at: String(data.published_at),
    locked: data.locked,
    favorite: data.favorite,
    liked: data.liked,
    likes: data.likes_count || 0,
    comments_count: data.comments_count || 0,
    new: data.new,
    views: data.views,
    autoLoadComments: data.autoLoadComments || data.auto_load_comments
  }

  if (data.author) {
    article.author = {
      id: Number(data.author.id),
      name: {
        first: String(data.author.name.first),
        full: String(data.author.name.full),
        last: String(data.author.name.last)
      },
      photo: String(data.author?.photo?.url || '')
    }
  }

  if (typeof article.autoLoadComments === 'undefined') {
    article.autoLoadComments = true
  }

  if (data.main_category) {
    article.main_category = {
      id: data.main_category.id,
      title: data.main_category.title,
      created_at: data.main_category.created_at
    }

    if (data.main_category.community) {
      article.main_category.community = {
        id: data.main_category.community.id,
        external_id: data.main_category.community.external_id,
        slug: data.main_category.community.slug,
        status: data.main_category.community.status,
        title: data.main_category.community.title
      }
    }
  }

  return article
}
export default {
  toClient (data: unknown): Article {
    return formattedArticle(data)
  },
  getPagination (data: unknown): Pagination {
    const d = data as Record<string, string | undefined>
    const pagination: Pagination = {}
    if (d['hydra:first']) {
      pagination.first = d['hydra:first']
    }
    if (d['hydra:last']) {
      pagination.last = d['hydra:last']
    }
    if (d['hydra:previous']) {
      pagination.previous = d['hydra:previous']
    }
    if (d['hydra:next']) {
      pagination.next = d['hydra:next']
    }

    return pagination
  }
}
